import React, { useEffect, useState } from "react";
import { FaEdit, FaTrash, FaPlus, FaSearch } from "react-icons/fa";
import { getData, postData, putData, deleteData } from "../services/FetchNodeservices";
import Swal from "sweetalert2";

export default function CompanyList() {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [formData, setFormData] = useState({
    id: "",
    name: "",
    gst_no: "",
    state: "",
    address: "",
    status: "active",
  });

  const [isEditing, setIsEditing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchCompanies = async () => {
    const response = await getData("api/company");
    if (response.success) {
      setCompanies(response.data);
      setFilteredCompanies(response.data);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    const q = searchQuery.toLowerCase();
    setFilteredCompanies(
      companies.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.gst_no?.toLowerCase().includes(q) ||
          c.state?.toLowerCase().includes(q) ||
          c.address?.toLowerCase().includes(q)
      )
    );
    setCurrentPage(1);
  }, [searchQuery, companies]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const res = isEditing
      ? await putData(`api/company/${formData.id}`, formData)
      : await postData("api/company", formData);

    if (res.success) {
      Swal.fire("Success", res.message, "success");
      setFormData({ id: "", name: "", gst_no: "", state: "", address: "", status: "active" });
      setIsEditing(false);
      fetchCompanies();
    }
  };

  const handleEdit = (company) => {
    setFormData(company);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Company?",
      icon: "warning",
      showCancelButton: true,
    });

    if (confirm.isConfirmed) {
      const res = await deleteData(`api/company/${id}`);
      if (res.success) {
        Swal.fire("Deleted!", "", "success");
        fetchCompanies();
      }
    }
  };

  const totalPages =
    rowsPerPage === "All" ? 1 : Math.ceil(filteredCompanies.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const displayed =
    rowsPerPage === "All"
      ? filteredCompanies
      : filteredCompanies.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className="ml-72 mt-16 p-6 bg-gray-100 min-h-screen">
      <div className="bg-white rounded-xl shadow-md p-6">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FaPlus className="text-blue-600" /> Company Management
          </h2>
        </div>

        {/* FORM */}
        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6"
        >
          <input className="input" placeholder="Company Name *" name="name" value={formData.name} onChange={handleChange} />
          <input className="input" placeholder="GST Number" name="gst_no" value={formData.gst_no} onChange={handleChange} />
          <input className="input" placeholder="State" name="state" value={formData.state} onChange={handleChange} />
          <input className="input col-span-1 md:col-span-2" placeholder="Address" name="address" value={formData.address} onChange={handleChange} />

          <select className="input" name="status" value={formData.status} onChange={handleChange}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4">
            {isEditing ? "Update Company" : "Add Company"}
          </button>
        </form>

        {/* SEARCH + ROWS */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center border rounded-lg px-3 py-1 w-1/3 bg-gray-50">
            <FaSearch className="text-gray-400 mr-2" />
            <input
              className="bg-transparent outline-none w-full"
              placeholder="Search company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <select
            className="border rounded-lg px-2 py-1"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(e.target.value === "All" ? "All" : parseInt(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value="All">All</option>
          </select>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="min-w-full border text-sm">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="th">#</th>
                <th className="th">Company</th>
                <th className="th">GST</th>
                <th className="th">State</th>
                <th className="th">Address</th>
                <th className="th">Status</th>
                <th className="th text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((c, i) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="td">{startIndex + i + 1}</td>
                  <td className="td font-medium">{c.name}</td>
                  <td className="td">{c.gst_no}</td>
                  <td className="td">{c.state}</td>
                  <td className="td max-w-xs truncate" title={c.address}>{c.address}</td>
                  <td className={`td capitalize ${c.status === "active" ? "text-green-600" : "text-red-600"}`}>
                    {c.status}
                  </td>
                  <td className="td flex justify-center gap-3">
                    <button onClick={() => handleEdit(c)} className="text-blue-600 hover:scale-110">
                      <FaEdit />
                    </button>
                    <button onClick={() => handleDelete(c.id)} className="text-red-600 hover:scale-110">
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {rowsPerPage !== "All" && (
          <div className="flex justify-center mt-4 gap-1">
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded ${
                  currentPage === i + 1 ? "bg-blue-600 text-white" : "bg-gray-200"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* TAILWIND HELPERS */}
      <style>
        {`
          .input {
            border: 1px solid #e5e7eb;
            padding: 8px 12px;
            border-radius: 8px;
            outline: none;
          }
          .th {
            padding: 10px;
            border-bottom: 1px solid #e5e7eb;
            text-align: left;
            font-weight: 600;
          }
          .td {
            padding: 10px;
            border-bottom: 1px solid #e5e7eb;
          }
        `}
      </style>
    </div>
  );
}
